import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import PageHero from "@/components/PageHero";
import { CheckCircle2, Send, Clock, Shield, Layers } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1400&q=80";

const provinces = [
  "British Columbia", "Alberta", "Saskatchewan", "Manitoba", "Ontario",
  "Quebec", "New Brunswick", "Nova Scotia", "PEI", "Newfoundland & Labrador",
  "Yukon", "Northwest Territories", "Nunavut", "Remote (Canada-wide)",
];

export default function ForEmployers() {
  const [form, setForm] = useState({
    company_name: "", contact_name: "", contact_email: "", phone: "",
    province: "British Columbia", role_type: "tech", roles_needed: "",
    engagement_model: "permanent", urgency: "30_days", additional_notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/employer-intake", form);
      toast.success("Brief received. A senior consultant will reach out within one business day.");
      setSubmitted(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="for-employers-page">
      <PageHero
        eyebrow="Hire Talent"
        title={<>Your next critical hire, <span className="italic" style={{ color: "var(--accent-teal)" }}>briefed in 20 minutes.</span></>}
        subtitle="Tell us about the role. A senior consultant responds within one business day with a market read, a compensation benchmark, and next steps."
        image={HERO_IMG}
        imageAlt="Canadian business meeting"
        testId="employers-hero"
      />

      <section className="py-16 md:py-24" data-testid="employer-form-section">
        <div className="max-w-[1280px] mx-auto px-5 md:px-10 lg:px-14 grid md:grid-cols-12 gap-10">
          <aside className="md:col-span-4 space-y-6">
            <div>
              <div className="eyebrow mb-3">What happens next</div>
              <h2 className="display-md">The Ashren engagement flow.</h2>
            </div>
            {[
              { icon: Send, title: "You submit the brief", desc: "Single form, two minutes. No procurement-RFP gauntlet." },
              { icon: Clock, title: "Consultant calls within 24 h", desc: "Senior consultant — not a junior coordinator — reads in and responds." },
              { icon: Layers, title: "Shortlist in ≤ 5 business days", desc: "3–5 pre-vetted candidates, with scorecard + calibration notes." },
              { icon: Shield, title: "Quality guarantee", desc: "Free replacement if the placement exits inside 90 days." },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.title} className="flex gap-4" data-testid={`employer-step-${i}`}>
                  <div className="w-10 h-10 rounded-lg shrink-0 flex items-center justify-center"
                       style={{ background: "rgba(29,180,222,0.14)", color: "var(--accent-teal)" }}>
                    <Icon size={20} />
                  </div>
                  <div>
                    <div className="font-semibold">{s.title}</div>
                    <p className="text-sm text-[color:var(--muted)] mt-1 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              );
            })}
          </aside>

          <div className="md:col-span-8">
            {submitted ? (
              <div className="card text-center py-16" data-testid="employer-submitted-card">
                <CheckCircle2 size={56} className="mx-auto mb-4" style={{ color: "var(--accent-mint)" }} />
                <h3 className="display-md mb-3">Brief received.</h3>
                <p className="text-[color:var(--muted)] max-w-md mx-auto">
                  A senior consultant will reach out to {form.contact_email} within one business day.
                  Keep an eye on your inbox — and check spam just in case.
                </p>
              </div>
            ) : (
              <form className="card space-y-5" onSubmit={submit} data-testid="employer-intake-form">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="field-label">Company name *</label>
                    <input required className="field-input" value={form.company_name} onChange={update("company_name")} data-testid="employer-field-company_name" />
                  </div>
                  <div>
                    <label className="field-label">Your name *</label>
                    <input required className="field-input" value={form.contact_name} onChange={update("contact_name")} data-testid="employer-field-contact_name" />
                  </div>
                  <div>
                    <label className="field-label">Work email *</label>
                    <input required type="email" className="field-input" value={form.contact_email} onChange={update("contact_email")} data-testid="employer-field-contact_email" />
                  </div>
                  <div>
                    <label className="field-label">Phone (optional)</label>
                    <input className="field-input" value={form.phone} onChange={update("phone")} data-testid="employer-field-phone" />
                  </div>
                  <div>
                    <label className="field-label">Province</label>
                    <select className="field-select" value={form.province} onChange={update("province")} data-testid="employer-field-province">
                      {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="field-label">Practice area</label>
                    <select className="field-select" value={form.role_type} onChange={update("role_type")} data-testid="employer-field-role_type">
                      <option value="tech">Technology (AI/ML, Cloud, Cyber, etc.)</option>
                      <option value="general">General (Finance, HR, Legal, etc.)</option>
                      <option value="healthcare">Healthcare (Nursing, Allied, Non-Clinical)</option>
                      <option value="rpo">RPO — Recruitment Process Outsourcing</option>
                      <option value="eor">EOR — Employer of Record (Canada)</option>
                      <option value="multiple">Multiple lanes</option>
                    </select>
                  </div>
                  <div>
                    <label className="field-label">Engagement model</label>
                    <select className="field-select" value={form.engagement_model} onChange={update("engagement_model")} data-testid="employer-field-engagement_model">
                      <option value="permanent">Permanent</option>
                      <option value="contract">Contract</option>
                      <option value="sow">Statement of Work (SOW)</option>
                      <option value="rpo">RPO / high-volume</option>
                    </select>
                  </div>
                  <div>
                    <label className="field-label">Urgency</label>
                    <select className="field-select" value={form.urgency} onChange={update("urgency")} data-testid="employer-field-urgency">
                      <option value="immediate">Immediate (this week)</option>
                      <option value="30_days">Within 30 days</option>
                      <option value="60_days">30–60 days</option>
                      <option value="exploring">Exploring / no deadline</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="field-label">Roles you&apos;re hiring for *</label>
                  <textarea required rows={4} className="field-textarea"
                    placeholder="e.g. Senior ML Platform Engineer (Toronto, $170-200k), Controller (Vancouver, permanent)…"
                    value={form.roles_needed} onChange={update("roles_needed")}
                    data-testid="employer-field-roles_needed" />
                </div>

                <div>
                  <label className="field-label">Additional context (optional)</label>
                  <textarea rows={3} className="field-textarea"
                    placeholder="Tech stack, team structure, remote policy, anything that helps us calibrate."
                    value={form.additional_notes} onChange={update("additional_notes")}
                    data-testid="employer-field-additional_notes" />
                </div>

                <div className="flex flex-wrap items-center gap-4 pt-2">
                  <button type="submit" disabled={submitting} className="btn-primary" data-testid="employer-submit-btn">
                    {submitting ? "Sending…" : "Submit brief"} <Send size={16} />
                  </button>
                  <p className="text-xs text-[color:var(--muted)]">
                    By submitting, you consent to Ashren contacting you. We never sell data. PIPEDA-compliant.
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
